// lib
import React, { useReducer, useState, useRef, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { isEqual } from 'lodash'

import { dataReducer } from '@aytm/app/stats/page/question-views/cq/reducers'

// components
import { default as ComponentContainer } from '@aytm/app/stats/page/question-views/components/Component'

import Batch from '@aytm/app/stats/page/question-views/cq/Batch'

import Controls from '@aytm/app/stats/page/question-views/cq/Controls'
import Footer from '@aytm/app/stats/page/question-views/components/component/Footer'

// styles
import style from '@aytm/app/stats/page/question-views/cq/styles/Component.module.scss'

const MAX_COMBINATIONS = 500

export const DataContext = React.createContext()

const Component = ({ model, data, states, disabled, onChange, onControlsChange, onUpdateData }) => {
  const [state, dispatch] = useReducer(dataReducer, {
    batches: JSON.parse(JSON.stringify(model.batches)),
    logicData: { items: {} },
    errors: []
  })

  const [changed, setChanged] = useState(false)

  const timer = useRef(null)

  const stateRef = useRef()
  stateRef.current = state

  const checkModel = () => {
    const newData = {}
    if (!isEqual(state.batches, model.batches)) {
      newData.logicData = state.logicData
      newData.errors = JSON.parse(JSON.stringify(state.errors))
      for (const bi in state.batches) {
        delete newData.logicData.items[`b${Number(bi) + 1}`]
        const index = newData.errors.indexOf(`b${Number(bi) + 1}`)
        0 <= index && newData.errors.splice(index, 1)
      }
      newData.batches = JSON.parse(JSON.stringify(model.batches))
    }
    return newData
  }
  const checkState = () => {
    const newData = {}
    if (!isEqual(state.batches, model.batches)) newData.batches = JSON.parse(JSON.stringify(state.batches))
    return newData
  }

  const checkError = () => {
    if (0 < state.errors.length || 2 != state.batches.length) return { error: true }
    if (state.batches.some(b => !b.answerVariants.length || b.answerVariants.some(av => !av.pattern.length)))
      return { error: true }
    if (MAX_COMBINATIONS < state.batches[0].answerVariants.length * state.batches[1].answerVariants.length)
      return {
        error: `Your selection exceeds the allowed number of combinations (${MAX_COMBINATIONS}). Please limit the answer choices for your banner and/or attribute, or choose a different banner/attribute combination.`
      }
    return {
      error: false
    }
  }

  const onReverse = () => {
    dispatch({
      type: 'REVERSE_BATCHES'
    })
  }

  useEffect(() => {
    if (changed) return

    dispatch({
      type: 'RESET',
      data: { ...checkModel() }
    })

    setChanged(!isEqual(stateRef.current.batches, model.batches))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.batches])

  useEffect(() => {
    setChanged(true)

    const newData = {
      logicData: state.logicData,
      ...checkError(),
      ...checkState()
    }

    timer.current && clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      onChange(newData)

      setChanged(false)
    }, 300)

    return () => timer.current && clearTimeout(timer.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  const dataContextValue = useMemo(() => {
    return {
      data,
      disabled,
      dispatch
    }
  }, [data, disabled, dispatch])

  return (
    <ComponentContainer
      Controls={Controls}
      model={model}
      data={data}
      states={states}
      changed={changed}
      disabled={disabled}
      onChange={onControlsChange}
    >
      <div className={style.component}>
        <div className={`${style.row} p-fluid p-col-12`}>
          <div className={`${style.label} p-field p-col-5`}>
            <b>1. Choose a banner</b> - answers to a certain question, traits
          </div>
          <div className={`${style.label} p-field p-col-5`}>
            <b>2. Choose an attribute</b> to check for correlations
          </div>
        </div>
        <div className={`${style.list} ${style.row} p-fluid p-col-12`}>
          <DataContext.Provider value={dataContextValue}>
            <div className={`${style.column} ${style['column-av']} p-fluid p-field p-col-5`}>
              <Batch item={state.batches[0]} position={1} />
            </div>
            <div className={`${style.column} p-fluid p-field p-col`}>
              <span className={style.toggle} onClick={disabled ? null : onReverse}>
                <i />
                <i />
                <i />
              </span>
            </div>
            <div className={`${style.column} ${style['column-av']} p-fluid p-field p-col-5`}>
              <Batch item={state.batches[1]} position={2} />
            </div>
          </DataContext.Provider>
        </div>
      </div>
      {!disabled && <Footer model={model} data={data} states={states} onUpdateData={onUpdateData} />}
    </ComponentContainer>
  )
}

Component.propTypes = {
  model: PropTypes.object,
  data: PropTypes.object,
  states: PropTypes.object,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onControlsChange: PropTypes.func,
  onUpdateData: PropTypes.func
}

Component.displayName = 'Component'

export default Component
